import { createFileRoute } from "@tanstack/react-router"
import { BookOpen, Car, Receipt, type LucideIcon } from "lucide-react"

import { TRIAL_DAYS } from "@/brand/plan-numbers"
import { breadcrumbJsonLd, buildHead } from "@/core/seo"

import { Breadcrumb, CtaBand } from "../components/landing"
import { HeroBand, SectionTitle } from "../components/landing/page-parts"
import { GLOSSARY_GROUPS, getGlossaryEntry } from "../content/glossary/entries"

/** Copy source: docs/design/landing/copy.md section 2.12 and the web-landing-glossary design. */
export const glossaryIndexSeo = () =>
  buildHead({
    title: "Tax and logbook glossary for sole traders",
    description:
      "Plain-English definitions of ABN, GST, BAS, cents per kilometre, logbook and more for Australian sole traders, each with its ATO source.",
    path: "/glossary",
    jsonLd: [breadcrumbJsonLd([{ name: "Home", path: "/" }, { name: "Glossary" }])],
  })

export const Route = createFileRoute("/_brand-layout/glossary")({
  head: () => {
    const { meta, links, scripts } = glossaryIndexSeo()
    return { meta, links, scripts }
  },
  component: GlossaryIndexPage,
})

const linkClass =
  "focus-visible:ring-ring inline-flex min-h-11 items-center rounded-sm font-semibold underline underline-offset-2 outline-none focus-visible:ring-2 md:min-h-8"

/** One icon per group, by position (the design: receipt for business and tax, car for car and logbook). */
const GROUP_ICONS: { icon: LucideIcon; tone: "primary" | "success" }[] = [
  { icon: Receipt, tone: "primary" },
  { icon: Car, tone: "success" },
]

export function GlossaryIndexPage() {
  return (
    <>
      <Breadcrumb items={[{ label: "Home", href: "/" }, { label: "Glossary" }]} />
      <HeroBand>
        <h1 className="text-3xl font-bold tracking-tight md:text-5xl">
          Tax and logbook terms for sole traders, in plain English
        </h1>
        <p className="text-muted-foreground max-w-[60ch] text-lg">
          Short definitions of the words you meet when you keep a logbook, issue tax invoices and do
          your BAS. Each one links to the ATO page it comes from.
        </p>
      </HeroBand>
      {GLOSSARY_GROUPS.map((group, k) => (
        <section
          key={group.title}
          className="px-band grid gap-4 py-8"
          aria-labelledby={`g-${group.slugs[0]}`}
        >
          <SectionTitle
            id={`g-${group.slugs[0]}`}
            icon={GROUP_ICONS[k]?.icon ?? BookOpen}
            tone={GROUP_ICONS[k]?.tone}
          >
            {group.title}
          </SectionTitle>
          <dl className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {group.slugs.map((slug) => {
              const entry = getGlossaryEntry(slug)
              if (!entry) return null
              return (
                <div key={slug} className="border-border bg-card rounded-lg border p-4">
                  <dt>
                    <a href={`/glossary/${slug}`} className={linkClass}>
                      {entry.term}
                    </a>
                  </dt>
                  <dd className="text-muted-foreground mt-1">{entry.definition}</dd>
                </div>
              )
            })}
          </dl>
        </section>
      ))}
      <CtaBand
        title="Put the terms to work"
        description={`${TRIAL_DAYS}-day free trial. No card needed.`}
        ctaLabel="Start free trial"
        ctaHref="/login"
      />
    </>
  )
}
